<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { RouterView } from 'vue-router';

import { useBranding } from '../composables/useBranding';

const branding = useBranding();

const authLayoutStyle = computed<Record<string, string>>(() => {
  const style: Record<string, string> = {
    '--auth-primary': branding.colors.primary,
    '--auth-secondary': branding.colors.secondary,
    '--auth-accent': branding.colors.accent ?? branding.colors.primary,
  };
  if (branding.logo.loginBackground) {
    style['--auth-background-image'] = `url("${branding.logo.loginBackground}")`;
  }
  return style;
});

interface ScheduleItem {
  id: string;
  title: string;
  badge: string;
  time: string;
  loc: string;
}

interface DocumentWidgetData {
  title: string;
  content: string;
}

interface AttendanceItem {
  name: string;
  type: string;
  status: string;
}

type ScheduleScenarioWidget = { type: 'schedule'; data: ScheduleItem[] };
type DocumentScenarioWidget = { type: 'document'; data: DocumentWidgetData };
type AttendanceScenarioWidget = { type: 'attendance'; data: AttendanceItem[] };

type FeedWidget =
  | ({ id: string } & ScheduleScenarioWidget)
  | ({ id: string } & DocumentScenarioWidget)
  | ({ id: string } & AttendanceScenarioWidget);

type ScenarioWidget = ScheduleScenarioWidget | DocumentScenarioWidget | AttendanceScenarioWidget;

interface HeroScenario {
  userText: string;
  botText: string;
  widget: ScenarioWidget;
}

function createFeedWidget(id: string, widget: ScenarioWidget): FeedWidget {
  if (widget.type === 'schedule') {
    return { id, type: 'schedule', data: widget.data };
  }
  if (widget.type === 'document') {
    return { id, type: 'document', data: widget.data };
  }
  return { id, type: 'attendance', data: widget.data };
}

const baseSchedule: ScheduleItem[] = [
  { id: '1', title: '제품 리뷰 회의', badge: '', time: '오늘 11:00', loc: '온라인' },
  { id: '2', title: '영업 주간 점검', badge: '', time: '오늘 16:30', loc: '회의실 A' },
];

const updatedSchedule: ScheduleItem[] = [
  { id: '0', title: '주간 마케팅 미팅', badge: 'NEW', time: '내일 15:00', loc: '회의실 B' },
  ...baseSchedule,
];

const scenarios: HeroScenario[] = [
  {
    userText: '내일 오후 3시 마케팅팀 주간 미팅 잡아줘',
    botText: '회의실 B로 일정을 등록했어요. 참석자에게 초대를 보냈습니다.',
    widget: { type: 'schedule', data: updatedSchedule },
  },
  {
    userText: '방금 끝난 영업 점검 회의 요약해서 회의록 작성해줘',
    botText: '회의 녹음 음성을 바탕으로 회의록 초안을 작성했습니다.',
    widget: {
      type: 'document',
      data: {
        title: '영업 주간 점검 회의록 (초안).md',
        content:
          '1. 이번 주 매출 달성률 98%\n2. B2B 고객사 납기 지연 방어\n3. 다음 주 액션플랜: 신규 프로모션 기획',
      },
    },
  },
  {
    userText: '오늘 우리 팀 연차 쓴 사람 누구야?',
    botText: '오늘 디자인팀 휴가자는 김지영(연차), 이민수(오전반차) 총 2명입니다.',
    widget: {
      type: 'attendance',
      data: [
        { name: '김지영', type: '연차', status: '휴무' },
        { name: '이민수', type: '오전반차', status: '오후출근' },
        { name: '박준호', type: '정상근무', status: '업무중' },
      ],
    },
  },
];

const scenarioIndex = ref(0);
const typingText = ref('');

const messages = ref([
  { id: 'init', type: 'bot', text: '무엇을 도와드릴까요? 원하시는 작업을 말씀해 주세요.' },
]);

const feedWidgets = ref<FeedWidget[]>([
  { id: 'init-widget', type: 'schedule', data: baseSchedule },
]);

let msgIdCounter = 10;
let widgetIdCounter = 10;

let timeoutIds: number[] = [];
let typeIntervalId: number;

function clearTimers() {
  timeoutIds.forEach(clearTimeout);
  clearInterval(typeIntervalId);
  timeoutIds = [];
}

function playScenario() {
  clearTimers();
  typingText.value = '';

  const scenario = scenarios[scenarioIndex.value];
  if (!scenario) return;

  const text = scenario.userText;
  let charIndex = 0;

  timeoutIds.push(
    window.setTimeout(() => {
      typeIntervalId = window.setInterval(() => {
        typingText.value += text[charIndex];
        charIndex++;
        if (charIndex >= text.length) {
          clearInterval(typeIntervalId);
          timeoutIds.push(
            window.setTimeout(() => {
              typingText.value = '';

              messages.value.push({
                id: String(msgIdCounter++),
                type: 'user',
                text: scenario.userText,
              });
              if (messages.value.length > 5) messages.value.shift();

              timeoutIds.push(
                window.setTimeout(() => {
                  messages.value.push({
                    id: String(msgIdCounter++),
                    type: 'bot',
                    text: scenario.botText,
                  });
                  if (messages.value.length > 5) messages.value.shift();

                  timeoutIds.push(
                    window.setTimeout(() => {
                      feedWidgets.value.unshift(
                        createFeedWidget(String(widgetIdCounter++), scenario.widget),
                      );
                      if (feedWidgets.value.length > 4) feedWidgets.value.pop();

                      timeoutIds.push(
                        window.setTimeout(() => {
                          scenarioIndex.value = (scenarioIndex.value + 1) % scenarios.length;
                          playScenario();
                        }, 4000),
                      );
                    }, 600),
                  );
                }, 800),
              );
            }, 500),
          );
        }
      }, 55); // typing speed
    }, 1000),
  );
}

onMounted(() => {
  playScenario();
});

onUnmounted(() => {
  clearTimers();
});
</script>

<template>
  <!-- 배경 이미지 모드: 전체 화면 bg + 중앙 카드 -->
  <div v-if="branding.logo.loginBackground" class="auth-fullbg" :style="authLayoutStyle">
    <div class="auth-fullbg__overlay" />
    <section class="auth-fullbg__card" aria-label="로그인">
      <div
        class="auth-fullbg__logo-wrap"
        :class="{ 'auth-fullbg__logo-wrap--rect': branding.logo.loginLogoVariant === 'rect' }"
      >
        <img :src="branding.logo.favicon" alt="" class="auth-fullbg__logo-img" />
      </div>
      <h2 class="auth-fullbg__title">{{ branding.productName }}</h2>
      <p v-if="branding.companyName" class="auth-fullbg__subtitle">{{ branding.companyName }}</p>
      <div class="auth-fullbg__form">
        <RouterView />
      </div>
      <footer v-if="branding.copyrightText" class="auth-fullbg__footer">
        <small>{{ branding.copyrightText }}</small>
      </footer>
    </section>
  </div>

  <!-- 기본 모드: 좌우 분할 -->
  <div v-else class="auth-layout" :style="authLayoutStyle">
    <section class="auth-panel" aria-label="로그인">
      <div class="auth-card">
        <header class="auth-card__header">
          <span
            class="auth-card__logo"
            :class="{ 'auth-card__logo--rect': branding.logo.loginLogoVariant === 'rect' }"
          >
            <img :src="branding.logo.favicon" alt="" />
          </span>
          <div>
            <h2 class="auth-card__title">{{ branding.productName }}</h2>
            <p v-if="branding.companyName" class="auth-card__subtitle">
              {{ branding.companyName }}
            </p>
          </div>
        </header>
        <RouterView />
        <footer class="auth-card__footer">
          <small>{{ branding.copyrightText }}</small>
        </footer>
      </div>
    </section>

    <section class="auth-hero" aria-hidden="true">
      <div class="auth-hero__widgets">
        <!-- Chat Widget -->
        <div class="auth-widget chat-widget">
          <div class="auth-widget__header">
            <div class="chat-header-info">
              <div class="chat-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 13.4876 3.36093 14.891 4.00473 16.1287L3 21L7.87132 19.9953C9.10898 20.6391 10.5124 21 12 21Z"
                    fill="currentColor"
                  />
                  <circle cx="9" cy="11" r="1.5" fill="var(--auth-primary)" />
                  <circle cx="15" cy="11" r="1.5" fill="var(--auth-primary)" />
                  <path
                    d="M8 15C9 16.5 11.5 17 15 14"
                    stroke="var(--auth-primary)"
                    stroke-width="1.5"
                    stroke-linecap="round"
                  />
                </svg>
              </div>
              <div>
                <h3>AX Assistant</h3>
                <p>실시간 데이터 연결됨</p>
              </div>
            </div>
            <div class="status-dot"></div>
          </div>
          <div class="chat-body">
            <TransitionGroup name="chat-feed" tag="div" class="chat-feed-container">
              <div
                v-for="msg in messages"
                :key="msg.id"
                class="chat-message"
                :class="msg.type + '-message'"
              >
                {{ msg.text }}
              </div>
            </TransitionGroup>
          </div>
          <div class="chat-input-bar">
            <span class="chat-placeholder" :class="{ typing: typingText.length > 0 }">
              {{ typingText || '메시지를 입력하세요...' }}
              <span class="cursor"></span>
            </span>
            <button class="chat-send" :class="{ active: typingText.length > 0 }">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <!-- Dynamic Feed Right Widget -->
        <div class="feed-container">
          <TransitionGroup name="feed-anim" tag="div" class="widget-feed">
            <div v-for="widget in feedWidgets" :key="widget.id" class="auth-widget feed-widget">
              <!-- Schedule Widget -->
              <div v-if="widget.type === 'schedule'" class="widget-content">
                <div class="auth-widget__header">
                  <div class="schedule-header-info">
                    <div class="status-dot"></div>
                    <h3>오늘의 일정</h3>
                  </div>
                  <span class="schedule-update">실시간 업데이트</span>
                </div>
                <div class="schedule-table">
                  <div class="schedule-row header">
                    <span>일정</span>
                    <span>시간</span>
                    <span>장소</span>
                  </div>
                  <div
                    v-for="item in widget.data"
                    :key="item.id"
                    class="schedule-row item"
                    :class="{ 'new-item': item.badge }"
                  >
                    <span
                      >{{ item.title }}
                      <span v-if="item.badge" class="badge">{{ item.badge }}</span></span
                    >
                    <span>{{ item.time }}</span>
                    <span>{{ item.loc }}</span>
                  </div>
                </div>
              </div>

              <!-- Document Widget -->
              <div v-else-if="widget.type === 'document'" class="widget-content">
                <div class="auth-widget__header">
                  <div class="schedule-header-info">
                    <div class="status-dot doc-dot"></div>
                    <h3>문서 초안 생성</h3>
                  </div>
                </div>
                <div class="doc-preview">
                  <h4>{{ widget.data.title }}</h4>
                  <pre>{{ widget.data.content }}</pre>
                </div>
              </div>

              <!-- Attendance Widget -->
              <div v-else-if="widget.type === 'attendance'" class="widget-content">
                <div class="auth-widget__header">
                  <div class="schedule-header-info">
                    <div class="status-dot att-dot"></div>
                    <h3>부서 근태 현황</h3>
                  </div>
                </div>
                <div class="att-table">
                  <div class="att-row header">
                    <span>이름</span>
                    <span>근태</span>
                    <span>상태</span>
                  </div>
                  <div v-for="user in widget.data" :key="user.name" class="att-row item">
                    <span>{{ user.name }}</span>
                    <span>{{ user.type }}</span>
                    <span :class="'status-' + (user.status === '휴무' ? 'off' : 'on')">{{
                      user.status
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </TransitionGroup>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
/* ── 배경 이미지 모드 ── */
.auth-fullbg {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: var(--auth-background-image) center / cover no-repeat;
}

.auth-fullbg__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    120deg,
    color-mix(in srgb, var(--auth-primary) 28%, transparent) 0%,
    rgba(15, 23, 42, 0.12) 45%,
    rgba(15, 23, 42, 0.32) 100%
  );
}

.auth-fullbg__card {
  position: relative;
  z-index: 1;
  width: min(92vw, 420px);
  padding: 44px 48px 36px;
  /* 서리유리(frosted glass): 어두운 배경에서도 어두운 글자 대비를 확보하도록
     흰색 불투명도를 충분히 높게 유지하고 강한 backdrop blur 로 유리 질감을 준다.
     backdrop-filter 미지원 브라우저는 background 불투명도(0.9)로 가독성 보존. */
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(24px) saturate(140%);
  -webkit-backdrop-filter: blur(24px) saturate(140%);
  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 20px;
  box-shadow:
    0 32px 80px rgba(0, 0, 0, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.7);
  text-align: center;
}

/* backdrop-filter 지원 시 배경 이미지가 카드 뒤로 살짝 비치도록 약간만 투명하게.
   0.82 는 어두운 배경 위 어두운 라벨/문구의 대비를 지키는 하한값이다. */
@supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  .auth-fullbg__card {
    background: rgba(255, 255, 255, 0.82);
  }
}

.auth-fullbg__logo-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 90px;
  height: 90px;
  margin: 0 auto 16px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.14);
  overflow: hidden;
}

.auth-fullbg__logo-img {
  width: 78px;
  height: 78px;
  object-fit: contain;
}

/* rect 변형: 가로형/직사각 로고 — 원형 칩 제거, 원본 비율 그대로 확대 */
.auth-fullbg__logo-wrap--rect {
  width: auto;
  height: auto;
  max-width: 240px;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
  overflow: visible;
}

.auth-fullbg__logo-wrap--rect .auth-fullbg__logo-img {
  width: auto;
  height: 104px;
  max-width: 240px;
}

.auth-fullbg__title {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 700;
  color: var(--el-text-color-primary);
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.auth-fullbg__subtitle {
  margin: 0 0 20px;
  font-size: 13px;
  color: var(--el-text-color-secondary);
}

.auth-fullbg__form {
  text-align: left;
  margin-top: 20px;
}

.auth-fullbg__footer {
  margin-top: 20px;
  color: var(--el-text-color-placeholder);
  font-size: 11px;
}

/* ── 기본 2분할 모드 ── */
.auth-layout {
  display: grid;
  grid-template-columns: minmax(380px, 460px) minmax(0, 1fr);
  min-height: 100vh;
  background: var(--el-bg-color-page);
}

.auth-hero {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  overflow: hidden;
  padding: 42px;
  color: #ffffff;
  background:
    radial-gradient(
      circle at 18% 20%,
      color-mix(in srgb, var(--auth-accent) 42%, transparent),
      transparent 26%
    ),
    radial-gradient(
      circle at 86% 78%,
      color-mix(in srgb, var(--auth-primary) 50%, transparent),
      transparent 30%
    ),
    linear-gradient(
      145deg,
      color-mix(in srgb, var(--auth-secondary) 92%, #0f172a),
      var(--auth-primary)
    );
}

.auth-hero::after {
  position: absolute;
  inset: 0;
  content: '';
  background:
    linear-gradient(90deg, rgba(255, 255, 255, 0.08) 1px, transparent 1px),
    linear-gradient(0deg, rgba(255, 255, 255, 0.08) 1px, transparent 1px);
  background-size: 44px 44px;
  mask-image: linear-gradient(135deg, #000 0%, transparent 70%);
  pointer-events: none;
}

.auth-hero__topbar,
.auth-hero__surface {
  position: relative;
  z-index: 1;
}

.auth-hero__topbar {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  font-weight: 700;
  font-size: 15px;
}

.auth-hero__logo,
.auth-card__logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.18);
}

.auth-hero__logo {
  width: 44px;
  height: 44px;
  border-radius: 10px;
}

.auth-card__logo {
  width: 46px;
  height: 46px;
  border-radius: 10px;
}

.auth-hero__logo img,
.auth-card__logo img {
  width: 32px;
  height: 32px;
}

/* rect 변형: 분할 레이아웃 카드 헤더에서도 칩 없이 원본 비율 확대 */
.auth-card__logo--rect {
  width: auto;
  height: 56px;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}

.auth-card__logo--rect img {
  width: auto;
  height: 100%;
}

.auth-hero__product {
  text-shadow: 0 8px 24px rgba(15, 23, 42, 0.2);
}

.auth-hero__widgets {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 32px;
  width: 100%;
  max-width: 900px;
  perspective: 1000px;
}

.auth-widget {
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 28px;
  box-shadow: 0 32px 64px rgba(0, 0, 0, 0.25);
  color: #fff;
  display: flex;
  flex-direction: column;
  animation: float 6s ease-in-out infinite;
  transform-style: preserve-3d;
}

.chat-widget {
  width: 380px;
  animation-delay: 0s;
  height: 520px;
}

.feed-container {
  width: 440px;
  height: 600px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  mask-image: linear-gradient(to bottom, black 65%, transparent 100%);
  -webkit-mask-image: linear-gradient(to bottom, black 65%, transparent 100%);
  padding-top: 10px;
  padding-bottom: 20px;
}

.widget-feed {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

.feed-widget {
  flex-shrink: 0;
}

.feed-anim-move,
.feed-anim-enter-active,
.feed-anim-leave-active {
  transition: all 0.7s cubic-bezier(0.16, 1, 0.3, 1);
}
.feed-anim-enter-from {
  opacity: 0;
  transform: translateY(-40px) scale(0.95);
}
.feed-anim-leave-to {
  opacity: 0;
  transform: translateY(40px) scale(0.95);
}

.widget-content {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-16px);
  }
}

.auth-widget__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  padding-bottom: 16px;
}

.chat-header-info {
  display: flex;
  align-items: center;
  gap: 14px;
}

.chat-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #fff;
  color: var(--auth-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
.chat-icon svg {
  width: 28px;
  height: 28px;
}

.chat-header-info h3,
.schedule-header-info h3 {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.chat-header-info p {
  margin: 4px 0 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 10px #10b981;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.2);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.schedule-header-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.schedule-update {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
}

.chat-body {
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: flex-end;
  overflow: hidden;
  mask-image: linear-gradient(to top, black 70%, transparent 100%);
  -webkit-mask-image: linear-gradient(to top, black 70%, transparent 100%);
  margin-bottom: 16px;
}

.chat-feed-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.chat-message {
  padding: 14px 18px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 1.5;
  max-width: 85%;
  flex-shrink: 0;
}

.chat-feed-move,
.chat-feed-enter-active,
.chat-feed-leave-active {
  transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}
.chat-feed-enter-from {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}
.chat-feed-leave-to {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}
.chat-feed-leave-active {
  position: absolute;
  width: 100%;
}

.user-message {
  background: var(--auth-primary);
  color: #fff;
  align-self: flex-end;
  border-bottom-right-radius: 4px;
  box-shadow: 0 4px 16px color-mix(in srgb, var(--auth-primary) 40%, transparent);
}

.bot-message {
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  align-self: flex-start;
  border-bottom-left-radius: 4px;
}

.chat-input-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 24px;
  padding: 6px 6px 6px 18px;
  transition: background 0.3s;
}

.chat-input-bar:hover {
  background: rgba(255, 255, 255, 0.2);
}

.chat-placeholder {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
}
.chat-placeholder.typing {
  color: #fff;
}

.cursor {
  display: inline-block;
  width: 2px;
  height: 14px;
  background: #fff;
  margin-left: 2px;
  vertical-align: middle;
  animation: blink 1s step-end infinite;
}

@keyframes blink {
  50% {
    opacity: 0;
  }
}

.chat-send {
  background: var(--auth-primary);
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  cursor: pointer;
  transition:
    transform 0.2s,
    box-shadow 0.2s;
}
.chat-send:hover,
.chat-send.active {
  transform: scale(1.05);
  box-shadow: 0 4px 12px color-mix(in srgb, var(--auth-primary) 50%, transparent);
}
.chat-send svg {
  width: 18px;
  height: 18px;
}

.schedule-table {
  display: flex;
  flex-direction: column;
}

.schedule-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  padding: 16px 12px;
  font-size: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition:
    background 0.2s,
    transform 0.2s;
  cursor: default;
}
.schedule-row:last-child {
  border-bottom: none;
}

.schedule-table {
  display: flex;
  flex-direction: column;
}

.schedule-row.header {
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  padding-bottom: 10px;
  padding-top: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  font-weight: 600;
}

.schedule-row.item {
  width: 100%;
}

.schedule-row.item:hover {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  transform: translateX(4px);
}

.new-item {
  background: rgba(16, 185, 129, 0.1);
}

.badge {
  background: #10b981;
  color: #fff;
  font-size: 10px;
  padding: 3px 8px;
  border-radius: 12px;
  margin-left: 8px;
  font-weight: 800;
  vertical-align: middle;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.4);
}

.doc-dot {
  background: #3b82f6;
  box-shadow: 0 0 10px #3b82f6;
}

.doc-preview {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.doc-preview h4 {
  margin: 0 0 12px;
  font-size: 14px;
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  padding-bottom: 10px;
}

.doc-preview pre {
  margin: 0;
  font-family: inherit;
  font-size: 13.5px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.85);
  white-space: pre-wrap;
}

.att-dot {
  background: #f59e0b;
  box-shadow: 0 0 10px #f59e0b;
}

.att-table {
  display: flex;
  flex-direction: column;
}

.att-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  padding: 16px 12px;
  font-size: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.att-row:last-child {
  border-bottom: none;
}

.att-row.header {
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  padding-bottom: 10px;
  padding-top: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  font-weight: 600;
}

.status-off {
  color: #f87171;
  font-weight: 600;
}
.status-on {
  color: #34d399;
  font-weight: 600;
}

.auth-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 36px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(255, 255, 255, 0.98)),
    var(--el-bg-color);
  box-shadow: 18px 0 50px rgba(15, 23, 42, 0.08);
}

.auth-card {
  width: min(100%, 370px);
}

.auth-card__header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 28px;
}

.auth-card__title {
  margin: 0 0 5px;
  color: var(--el-text-color-primary);
  font-size: 25px;
  line-height: 1.1;
}

.auth-card__subtitle {
  margin: 0;
  color: var(--el-text-color-secondary);
  font-size: 14px;
}

.auth-card__footer {
  margin-top: 22px;
  color: var(--el-text-color-secondary);
  font-size: 12px;
}

@media (max-width: 860px) {
  .auth-layout {
    grid-template-columns: 1fr;
    background:
      linear-gradient(
        145deg,
        color-mix(in srgb, var(--auth-primary) 20%, transparent),
        transparent
      ),
      var(--el-bg-color-page);
  }

  .auth-hero {
    display: none;
  }

  .auth-panel {
    min-height: 100vh;
    padding: 24px;
    background:
      linear-gradient(180deg, rgba(255, 255, 255, 0.92), rgba(255, 255, 255, 0.98)),
      var(--el-bg-color);
  }
}
</style>

<style>
/* html.dark 오버라이드 — scoped `:global(html.dark) .foo` 접두 셀렉터는 적용되지
   않는 경우가 실측 확인돼(POP 카드 보드 동일 함정) unscoped 블록에 둔다. */
html.dark .auth-panel {
  background:
    linear-gradient(180deg, rgba(17, 24, 39, 0.9), rgba(17, 24, 39, 0.98)), var(--el-bg-color);
  box-shadow: 18px 0 50px rgba(0, 0, 0, 0.35);
}

/* 배경 이미지 모드 — 다크에선 사진 위 오버레이를 어둡게, 카드는 다크 서리유리로.
   타이틀/서브타이틀/입력은 EP dark 변수(--el-*)가 자동 처리한다. */
html.dark .auth-fullbg__overlay {
  background: linear-gradient(
    120deg,
    rgba(2, 6, 23, 0.55) 0%,
    rgba(2, 6, 23, 0.62) 45%,
    rgba(2, 6, 23, 0.78) 100%
  );
}

html.dark .auth-fullbg__card {
  background: rgba(15, 23, 42, 0.92);
  border-color: rgba(148, 163, 184, 0.25);
  box-shadow:
    0 32px 80px rgba(0, 0, 0, 0.6),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
}

@supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  html.dark .auth-fullbg__card {
    background: rgba(15, 23, 42, 0.84);
  }
}

html.dark .auth-fullbg__logo-wrap {
  background: #f8fafc;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.55);
}
</style>
